/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { get, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core/types/symbols';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';
import type AttendanceCodesService from 'linced-app/services/attendance-codes';

// Proto: attendance.AttendanceAnalysis.
export default class Analysis extends Model {
  declare [Type]: 'attendance/analysis';

  @service
  declare attendanceCodes: AttendanceCodesService;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.id')
  declare person_id: string | null | undefined;

  @attr({ defaultValue: () => ({}) })
  declare counts: Record<string, number>;

  @attr('number', { defaultValue: 0 })
  declare total: number;

  getCodeCount(codes: readonly { code: string }[]) {
    let sum = 0;
    codes.forEach((code) => {
      const count = get(this.counts, code.code) ?? 0;
      sum += count;
    });

    if (sum > 0) {
      return sum;
    }
  }

  @computed('counts')
  get truancyP() {
    // TS-STANDIN(attendance-codes): service is JS, so truancyPCodes infers as
    // any.
    return this.getCodeCount(
      this.attendanceCodes.truancyPCodes as { code: string }[],
    );
  }

  @computed('counts')
  get truancyJ() {
    // TS-STANDIN(attendance-codes): service is JS, so truancyJCodes infers as
    // any.
    return this.getCodeCount(
      this.attendanceCodes.truancyJCodes as { code: string }[],
    );
  }

  @computed('counts')
  get truancyU() {
    // TS-STANDIN(attendance-codes): service is JS, so truancyUCodes infers as
    // any.
    return this.getCodeCount(
      this.attendanceCodes.truancyUCodes as { code: string }[],
    );
  }

  @computed('counts')
  get truancyO() {
    // TS-STANDIN(attendance-codes): service is JS, so truancyOCodes infers as
    // any.
    return this.getCodeCount(
      this.attendanceCodes.truancyOCodes as { code: string }[],
    );
  }

  @computed('counts')
  get presentP() {
    // TS-STANDIN(attendance-codes): service is JS, so presentPCodes infers as
    // any.
    return this.getCodeCount(
      this.attendanceCodes.presentPCodes as { code: string }[],
    );
  }

  @computed('counts')
  get presentA() {
    // TS-STANDIN(attendance-codes): service is JS, so presentACodes infers as
    // any.
    return this.getCodeCount(
      this.attendanceCodes.presentACodes as { code: string }[],
    );
  }

  @computed('counts.L', 'total')
  get latePercentage() {
    const late = get(this.counts, 'L') ?? 0;
    if (late > 0) {
      return ((late / this.total) * 100).toFixed(1);
    }
  }

  @computed('presentA', 'total')
  get absentPercentage() {
    if (this.presentA! > 0) {
      return ((this.presentA! / this.total) * 100).toFixed(1);
    }
  }

  @computed('presentP', 'total')
  get presentPercentage() {
    if (this.presentP! > 0) {
      return ((this.presentP! / this.total) * 100).toFixed(1);
    }
  }
}
